import classNames from "classnames";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import Button from "components/button/Button";
import topMarin from "components/dashboard/overview/manageWidgetDialog.scss";
import Info from "components/icons/Info";
import OpenInNewTab from "components/icons/OpenInNewTab";
import CircleNumber from "components/products/CircleWithNumber";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import Tooltip from "components/tooltip/Tooltip";
import { BmpOpOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import utilsStyle from "styles/utils.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface Props {
    stepIndex: number;
    menuExpanded: boolean;
    stepCompleted: boolean;
    stepActive: boolean;
    stepClickable: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
}

interface Step {
    description?: string | JSX.Element;
    content?: JSX.Element;
    secondDescription?: string;
    icon?: string;
    tooltip?: string;
    subStep?: string[];
}

const SystemPreparation = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [showContent, setShowContent] = React.useState<boolean>(false);
    const stepName = BmpOpOnboardingSteps.STEP_SYSTEM_PREPARATION;
    const theme = useTheme();

    const clickEvent = () => {
        props.stepClickable && setShowContent(!showContent);
    };

    const saveOnboardingStep = () => {
        props.saveStepInProgressIndex(props.stepIndex);
        props.saveOnboardingStep({
            product: OnboardingProductType.BMPOP,
            step: BmpOpOnboardingSteps.STEP_SYSTEM_PREPARATION,
        });
    };

    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
        }
    }, [props.stepInProgressIndex]);

    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    const RequirementsTable = () => (
        <div className={style.requirementsTable}>
            <table>
                <thead>
                    <tr>
                        <th>
                            {t(
                                "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.component"
                            )}
                        </th>
                        <th>
                            <div className={style.headerWithIcon}>
                                {t(
                                    "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.smallScaleOperations"
                                )}

                                <Tooltip
                                    content={t(
                                        "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.smallScaleOperationsToolTip"
                                    )}
                                    placement={"top"}
                                >
                                    <span className={style.tooltipSpacing}>
                                        <Info
                                            borderColor={theme.contentBackgroundColor}
                                            color={theme.sortIconActiveColor}
                                            size={"14"}
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                        </th>
                        <th>
                            <div className={style.headerWithIcon}>
                                {t(
                                    "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.largeScaleOperations"
                                )}
                                <Tooltip
                                    content={t(
                                        "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.largeScaleOperationsToolTip"
                                    )}
                                    placement={"top"}
                                >
                                    <span className={style.tooltipSpacing}>
                                        <Info
                                            borderColor={theme.contentBackgroundColor}
                                            color={theme.sortIconActiveColor}
                                            size={"14"}
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            {t("Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.cpu")}
                        </td>
                        <td>
                            {t(
                                "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.dualCore",
                                "Dual Core processor"
                            )}
                        </td>
                        <td>
                            {t(
                                "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.quadCore",
                                "2x Quad Core processor"
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>
                            {t(
                                "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.memory"
                            )}
                        </td>
                        <td>
                            {t(
                                "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.fourGb"
                            )}
                        </td>
                        <td>
                            {t(
                                "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.twelveGb"
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );

    function openJavaDownloadManualInNewTab() {
        window.open(
            "https://support.blancco.com/space/KB/157745189/Using+proxy+with+Blancco+Management+Portal+-+On-Premise",
            "_blank"
        );
    }

    const systemPreparationSteps: Step[] = [
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.systemPreparation.operatingSystemRequirements"),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.systemPreparation.prerequisiteRequirements.title"),
            content: <RequirementsTable />,
        },
        {
            description: (
                <>
                    <Trans
                        i18nKey="Onboarding.bmpOp.gettingStarted.steps.systemPreparation.javaDownload1"
                        values={{
                            linkText: t("Onboarding.bmpOp.gettingStarted.steps.systemPreparation.openJdkText"),
                        }}
                        components={[
                            <a
                                key="javaLink"
                                title={t("Onboarding.bmpOp.gettingStarted.steps.systemPreparation.javaDownloadUrl")}
                                href={t("Onboarding.bmpOp.gettingStarted.steps.systemPreparation.javaDownloadUrl")}
                                target="_blank"
                                rel="noopener noreferrer"
                            />,
                        ]}
                    />
                </>
            ),
            content: (
                <>
                    <div className={classNames(style.leftMargin, topMarin.closeButton, utilsStyle.spacingTopMd)}>
                        <Tooltip content={t("Common.openInNewTab")}>
                            <button
                                onClick={() => openJavaDownloadManualInNewTab()}
                                data-testid={testIds.workArea.onboarding.step.openInNewTabButton}
                                data-type={"java-download-manual"}
                            >
                                <OpenInNewTab size={24} />
                                <strong className={style.blueText}>
                                    {t(
                                        "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.javaDownloadAdditionInformation"
                                    )}
                                </strong>
                            </button>
                        </Tooltip>
                    </div>
                </>
            ),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.systemPreparation.databaseInstall.title"),
            content: (
                <>
                    <ul className={style.blueBullets}>
                        <li>
                            <a
                                title={t(
                                    "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.databaseInstall.postgresDatabaseOption"
                                )}
                                href={t(
                                    "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.databaseInstall.postgresDownloadUrl"
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t(
                                    "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.databaseInstall.postgresDatabaseOption"
                                )}
                            </a>
                            {t("Onboarding.bmpOp.gettingStarted.steps.systemPreparation.databaseInstall.orText")}
                        </li>
                        <li>
                            <a
                                title={t(
                                    "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.databaseInstall.microsoftSqlDatabaseOption"
                                )}
                                href={t(
                                    "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.databaseInstall.microsoftSqlDownloadUrl"
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t(
                                    "Onboarding.bmpOp.gettingStarted.steps.systemPreparation.databaseInstall.microsoftSqlDatabaseOption"
                                )}
                            </a>
                        </li>
                    </ul>
                </>
            ),
        },
    ];

    function createContent() {
        return (
            <>
                <div className={style.stepsContainer}>
                    {systemPreparationSteps.map((step, index) => (
                        <div key={index} className={style.subStepsToFollowLayout}>
                            <CircleNumber number={index + 1} subStep={true} />
                            <div className={style.stepPadding}>
                                <div>
                                    {step.description}
                                    {step.secondDescription}
                                </div>
                                {step.content && <div className={style.stepPadding}>{step.content}</div>}
                                {step.subStep &&
                                    step.subStep.map((subStep, index) => (
                                        <div key={index} className={style.subStepPadding}>
                                            {subStep}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className={style.markComplete}>
                    {props.stepCompleted ? (
                        <StepCompletedText
                            successText={t("Onboarding.gettingStartedFlow.completed")}
                            testId={testIds.workArea.onboarding.step.statusLabel}
                            dataType={OnboardingProductType.BMPOP + "_" + stepName}
                        />
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            loadingAnimation={props.stepInProgressIndex === props.stepIndex}
                            disabled={props.stepInProgressIndex === props.stepIndex}
                            className={classNames(buttons.primaryButtonWithoutIcon)}
                            onClick={saveOnboardingStep}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={OnboardingProductType.BMPOP + "_" + stepName}
                        >
                            {props.stepInProgressIndex === props.stepIndex
                                ? t("Onboarding.saving")
                                : t("Onboarding.gettingStartedFlow.markAsCompleted")}
                        </Button>
                    )}
                </div>
            </>
        );
    }
    return (
        <StepHeader
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            title={t("Onboarding.bmpOp.gettingStarted.steps.systemPreparation.title")}
            stepIndex={props.stepIndex}
            menuExpanded={props.menuExpanded}
            showContent={showContent}
            clickEvent={clickEvent}
            content={createContent()}
            type={OnboardingProductType.BMPOP}
            stepName={stepName}
        />
    );
};

export default connector(SystemPreparation);
